import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Loader2, Lock, Wallet } from "lucide-react";
import { api } from "@/lib/api";
import { signEvmMessage } from "@/lib/onchain";
import { toast } from "sonner";

export default function AdminLogin() {
  const { admin, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [walletLoading, setWalletLoading] = useState(false);

  useEffect(() => {
    if (admin && admin.role === "admin") navigate("/admin");
  }, [admin, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/admin");
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Login failed");
    } finally { setLoading(false); }
  };

  // Admin access by signing with an allowlisted wallet (ADMIN_WALLETS env) —
  // no password, identity = wallet signature.
  const walletLogin = async () => {
    setError("");
    setWalletLoading(true);
    try {
      const { data: n } = await api.get("/auth/admin-nonce");
      const { address, signature } = await signEvmMessage(n.message);
      await api.post("/auth/admin-wallet", { nonce: n.nonce, address, signature });
      navigate("/admin");
    } catch (err) {
      if (err?.code === 4001) setError("Signature rejected in wallet");
      else {
        const d = err.response?.data?.detail || err.message;
        setError(typeof d === "string" ? d.slice(0, 120) : "Wallet login failed");
      }
    } finally { setWalletLoading(false); }
  };

  return (
    <div className="flex min-h-screen items-center justify-center grain px-4">
      <form onSubmit={submit} className="w-full max-w-sm rounded-2xl border border-white/10 bg-[#080808] p-8" data-testid="admin-login-form">
        <div className="mb-6 flex items-center gap-2">
          <span className="font-display text-2xl font-black tracking-tighter">FLIP</span>
          <span className="text-gold">↑</span>
          <span className="ml-auto flex items-center gap-1 text-xs text-zinc-500"><Lock className="h-3 w-3" /> Admin</span>
        </div>
        <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Email</label>
        <input data-testid="admin-email" value={email} onChange={(e) => setEmail(e.target.value)} type="email" required
          className="mb-4 mt-1 w-full rounded-lg border border-white/15 bg-black px-3 py-2.5 text-sm outline-none focus:border-white/40" />
        <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Password</label>
        <input data-testid="admin-password" value={password} onChange={(e) => setPassword(e.target.value)} type="password" required
          className="mb-4 mt-1 w-full rounded-lg border border-white/15 bg-black px-3 py-2.5 text-sm outline-none focus:border-white/40" />
        {error && <div className="mb-3 text-sm text-negative" data-testid="admin-login-error">{error}</div>}
        <button data-testid="admin-login-submit" disabled={loading || walletLoading}
          className="flex w-full items-center justify-center gap-2 rounded-full bg-white py-3 font-bold text-black transition-transform hover:bg-zinc-200 active:scale-95 disabled:opacity-40">
          {loading && <Loader2 className="h-4 w-4 animate-spin" />} Sign in
        </button>
        <div className="my-4 flex items-center gap-3 text-[10px] uppercase tracking-widest text-zinc-600">
          <span className="h-px flex-1 bg-white/10" /> or <span className="h-px flex-1 bg-white/10" />
        </div>
        <button type="button" data-testid="admin-wallet-login" disabled={loading || walletLoading} onClick={walletLogin}
          className="flex w-full items-center justify-center gap-2 rounded-full border border-white/20 py-3 font-bold text-white transition-colors hover:bg-white/5 disabled:opacity-40">
          {walletLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wallet className="h-4 w-4" />} Sign in with wallet
        </button>
        <p className="mt-2 text-center text-[10px] text-zinc-600">Your wallet must be in the ADMIN_WALLETS allowlist.</p>
      </form>
    </div>
  );
}
